import React, { useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api, { formatApiError } from "@/lib/api";
import { MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicLayout>
      <div className="min-h-[calc(100vh-200px)] bg-[#F8F9FA] flex items-center justify-center py-16 px-6">
        <div className="w-full max-w-md">
          <div className="bg-white border border-slate-200 p-10 rounded-sm">
            {sent ? (
              <div data-testid="forgot-sent" className="text-center">
                <MailCheck className="w-14 h-14 mx-auto text-[#10B981]" />
                <h1 className="font-display text-3xl uppercase tracking-tighter mt-5">Check Your Email</h1>
                <p className="text-sm text-slate-600 mt-3">
                  If an account exists for <span className="font-semibold">{email}</span>, we've sent a link to reset your password. The link expires in 1 hour.
                </p>
                <Link to="/login" className="inline-block mt-6 text-[#E63946] font-semibold uppercase tracking-wider text-xs">
                  Back to Sign In
                </Link>
              </div>
            ) : (
              <>
                <div className="label-overline text-[#E63946]">Password Reset</div>
                <h1 className="font-display text-4xl uppercase tracking-tighter mt-2">Forgot Password</h1>
                <p className="text-sm text-slate-600 mt-2">Enter your email and we'll send you a link to reset it.</p>

                {error && (
                  <div data-testid="forgot-error" className="mt-6 border-l-4 border-[#E63946] bg-red-50 p-3 text-sm text-[#E63946]">
                    {error}
                  </div>
                )}

                <form onSubmit={onSubmit} className="mt-6 space-y-4">
                  <div>
                    <label className="label-overline">Email</label>
                    <input
                      data-testid="forgot-email-input"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-2 w-full px-4 py-3 border border-slate-300 rounded-sm focus:border-[#0A192F]"
                    />
                  </div>
                  <button
                    data-testid="forgot-submit-button"
                    type="submit"
                    disabled={busy}
                    className="w-full bg-[#0A192F] hover:bg-[#1a2a4a] text-white py-3 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60"
                  >
                    {busy ? "Sending…" : "Send Reset Link"}
                  </button>
                </form>

                <div className="mt-6 text-center text-sm text-slate-600">
                  Remembered it?{" "}
                  <Link to="/login" className="text-[#E63946] font-semibold uppercase tracking-wider text-xs">
                    Sign In
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
